import { useState, useMemo } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  Vote,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Users,
  Trophy,
  BarChart3,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  Eye,
  Plus,
  Search,
  Target,
  Zap,
  Crown,
  Award,
  Ban,          // V3.1: For abort button
  AlertOctagon  // V3.1: For aborted status
} from 'lucide-react'
import { cn } from '../lib/utils'
import { UIProposal, UIVotingPower, formatAbortVoteStatus, hasUserVotedAbort, formatStrikeCount } from '../lib/poolDataTransformers'

/**
 * Props for the GovernancePanel component
 * V3.1: Added onVoteAbort handler for abort voting
 */
interface GovernancePanelProps {
  proposals: UIProposal[]
  votingPower: UIVotingPower
  userVotes: Record<string, 'for' | 'against' | 'abstain' | 'abort'>  // V3.1: Added 'abort' option
  loading?: boolean
  className?: string
  onVote: (proposalId: string, vote: 'for' | 'against' | 'abstain') => Promise<void>
  onVoteAbort?: (proposalId: string) => Promise<void>  // V3.1: New abort vote handler
  onCreateProposal: () => void
  onViewProposal: (proposalId: string) => void
  onExecuteProposal?: () => Promise<void>
  onAbortProposal?: (proposalId: string) => Promise<void>  // V3.1: Finalize abort (after 2 votes)
  // V3.1: User wallet address for checking abort eligibility
  userWallet?: string | undefined
}

/**
 * Enhanced GovernancePanel Component - Professional voting interface
 * 
 * This component provides:
 * - Premium glassmorphism governance interface
 * - Interactive voting cards with real-time updates
 * - Animated progress bars and vote counters
 * - Professional proposal management system
 * - Advanced filtering and sorting capabilities
 * - Responsive design optimized for all devices
 * - Accessibility-compliant voting interface
 * - Premium DeFi platform aesthetics
 * - V3.1: Abort voting functionality
 * 
 * Note (2026-01-13): Removed non-functional "Discuss" button from proposal cards
 * Note (2026-01-23): Added V3.1 abort voting support
 */
export function GovernancePanel({
  proposals,
  votingPower,
  userVotes,
  loading = false,
  className,
  onVote,
  onVoteAbort,
  onCreateProposal,
  onViewProposal,
  onExecuteProposal,
  onAbortProposal,
  userWallet
}: GovernancePanelProps): JSX.Element {
  const { connected } = useWallet()
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('active')
  const [sortBy, setSortBy] = useState<'newest' | 'ending' | 'popular'>('newest')
  const [searchTerm, setSearchTerm] = useState('')
  const [votingOnProposal, setVotingOnProposal] = useState<string | null>(null)
  const [executingProposal, setExecutingProposal] = useState(false)
  const [abortingProposal, setAbortingProposal] = useState(false)  // V3.1: Track abort state
  const [showVotingPower, _setShowVotingPower] = useState(true)

  /**
   * Filter and sort proposals based on current state
   * V3.1: 'aborted' is now included in completed filter
   */
  const filteredProposals = useMemo(() => {
    let filtered = proposals

    // Apply status filter
    if (filter === 'active') {
      filtered = filtered.filter(p => p.status === 'active')
    } else if (filter === 'completed') {
      // V3.1: Include 'aborted' in completed filter
      filtered = filtered.filter(p => ['passed', 'rejected', 'executed', 'expired', 'aborted'].includes(p.status))
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
        case 'ending':
          return new Date(a.endTime).getTime() - new Date(b.endTime).getTime()
        case 'popular':
          return b.totalVotes - a.totalVotes
        default:
          return 0
      }
    })

    return filtered
  }, [proposals, filter, sortBy, searchTerm])

  /**
   * Calculate voting progress percentage
   */
  const getVotingProgress = (proposal: UIProposal): number => {
    return Math.min((proposal.totalVotes / proposal.quorumRequired) * 100, 100)
  }

  /**
   * Get time remaining for active proposals
   */
  const getTimeRemaining = (endTime: Date): string => {
    const now = new Date()
    const diff = endTime.getTime() - now.getTime()
    
    if (diff <= 0) return 'Ended'
    
    const days = Math.floor(diff / (24 * 60 * 60 * 1000))
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
    const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000))

    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  /**
   * Get status styling and icon
   * V3.1: Added 'aborted' status styling
   */
  const getStatusDisplay = (status: UIProposal['status']) => {
    switch (status) {
      case 'active':
        return {
          color: 'text-status-info',
          bgColor: 'bg-status-info/20',
          borderColor: 'border-status-info/30',
          icon: Clock,
          text: 'Active'
        }
      case 'passed':
        return {
          color: 'text-status-success',
          bgColor: 'bg-status-success/20',
          borderColor: 'border-status-success/30',
          icon: CheckCircle2,
          text: 'Passed'
        }
      case 'rejected':
        return {
          color: 'text-status-error',
          bgColor: 'bg-status-error/20',
          borderColor: 'border-status-error/30',
          icon: XCircle,
          text: 'Rejected'
        }
      case 'executed':
        return {
          color: 'text-cyber-green',
          bgColor: 'bg-cyber-green/20',
          borderColor: 'border-cyber-green/30',
          icon: Trophy,
          text: 'Executed'
        }
      case 'expired':
        return {
          color: 'text-text-muted',
          bgColor: 'bg-dark-surface/50',
          borderColor: 'border-dark-border',
          icon: AlertTriangle,
          text: 'Expired'
        }
      // V3.1: New aborted status styling
      case 'aborted':
        return {
          color: 'text-cyber-magenta',
          bgColor: 'bg-cyber-magenta/20',
          borderColor: 'border-cyber-magenta/30',
          icon: AlertOctagon,
          text: 'Aborted'
        }
    }
  }

  /**
   * Get priority styling
   */
  const getPriorityColor = (priority: UIProposal['priority']): string => {
    switch (priority) {
      case 'critical': return 'text-status-error'
      case 'high': return 'text-cyber-magenta'
      case 'medium': return 'text-cyber-yellow'
      case 'low': return 'text-text-muted'
    }
  }

  /**
   * Get category icon
   */
  const getCategoryIcon = (category: UIProposal['category']) => {
    switch (category) {
      case 'funding': return Target
      case 'parameter': return BarChart3
      case 'upgrade': return Zap
      case 'treasury': return Trophy
    }
  }

  /**
   * Handle voting action
   */
  const handleVote = async (proposalId: string, vote: 'for' | 'against' | 'abstain') => {
    if (!connected || votingOnProposal) return

    setVotingOnProposal(proposalId)
    try {
      await onVote(proposalId, vote)
    } finally {
      setVotingOnProposal(null)
    }
  }

  /**
   * V3.1: Handle abort vote action
   * Votes abort on a proposal - once 2 wallets vote abort, proposal can be finalized as aborted
   */
  const handleVoteAbort = async (proposalId: string) => {
    if (!connected || votingOnProposal || !onVoteAbort) return

    setVotingOnProposal(proposalId)
    try {
      await onVoteAbort(proposalId)
    } finally {
      setVotingOnProposal(null)
    }
  }

  /**
   * V3.1: Handle abort finalization
   * Called after 2 wallets have voted abort to finalize the proposal abortion
   */
  const handleAbortProposal = async (proposalId: string) => {
    if (!connected || abortingProposal || !onAbortProposal) return

    setAbortingProposal(true)
    try {
      await onAbortProposal(proposalId)
    } finally {
      setAbortingProposal(false)
    }
  }

  /**
   * Handle proposal execution for passed proposals
   */
  const handleExecuteProposal = async () => {
    if (!connected || executingProposal || !onExecuteProposal) return

    setExecutingProposal(true)
    try {
      await onExecuteProposal()
    } finally {
      setExecutingProposal(false)
    }
  }

  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-48" />
          <div className="h-10 bg-gradient-to-r from-dark-surface to-dark-border rounded-cyber animate-shimmer w-32" />
        </div>
        
        <div className="grid gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="glass-card p-6 space-y-4">
              <div className="h-6 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-3/4" />
              <div className="h-4 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-full" />
              <div className="h-4 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-2/3" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-4 lg:space-y-6", className)}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-cyber-gradient rounded-cyber flex items-center justify-center">
            <Vote className="w-6 h-6 text-dark-bg" />
          </div>
          <div>
            <h3 className="text-2xl font-header text-gradient-cyber">Governance</h3>
            <p className="text-sm text-text-muted">
              {filteredProposals.length} proposals • {proposals.filter(p => p.status === 'active').length} active
            </p>
          </div>
        </div>

        <button
          onClick={onCreateProposal}
          disabled={!connected}
          className="cyber-button-primary flex items-center space-x-2 px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Create a new proposal"
        >
          <Plus className="w-4 h-4" />
          <span>Create Proposal</span>
        </button>
      </div>

      {/* Voting Power Card */}
      {connected && showVotingPower && (
        <div className="metric-card p-6 relative overflow-hidden group">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-cyber-purple/20 rounded-cyber flex items-center justify-center">
                <Crown className="w-6 h-6 text-cyber-purple" />
              </div>
              <div>
                <h4 className="text-lg font-header text-cyber-purple">Your Voting Power</h4>
                <p className="text-sm text-text-muted">Based on pool shares and participation</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 px-3 py-1 bg-cyber-purple/20 text-cyber-purple rounded-cyber text-sm">
                <Award className="w-3 h-3" />
                <span>#{votingPower.rank}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="metric-value text-cyber-purple group-hover:text-glow">
                {votingPower.totalShares.toFixed(2)}
              </div>
              <div className="text-sm text-text-secondary">Shares</div>
            </div>
            
            <div className="text-center">
              <div className="metric-value text-cyber-cyan group-hover:text-glow">
                {(votingPower.votingWeight * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-text-secondary">Voting Weight</div>
            </div>
            
            <div className="text-center">
              <div className="metric-value text-cyber-green group-hover:text-glow">
                {votingPower.totalVotedProposals}
              </div>
              <div className="text-sm text-text-secondary">Votes Cast</div>
            </div>
            
            <div className="text-center">
              <div className="metric-value text-cyber-yellow group-hover:text-glow">
                {(votingPower.participationRate * 100).toFixed(0)}%
              </div>
              <div className="text-sm text-text-secondary">Participation</div>
            </div>
          </div>

          {/* V3.1: Strike tracking display */}
          {(votingPower.abortStrikeCount > 0 || votingPower.proposeStrikeCount > 0) && (
            <div className="mt-4 pt-4 border-t border-dark-border/30">
              <div className="flex items-center justify-center space-x-6 text-sm">
                {votingPower.abortStrikeCount > 0 && (
                  <div className={cn(
                    "flex items-center space-x-2 px-3 py-1 rounded-cyber",
                    formatStrikeCount(votingPower.abortStrikeCount).level === 'danger'
                      ? 'bg-status-error/20 text-status-error'
                      : formatStrikeCount(votingPower.abortStrikeCount).level === 'warning'
                        ? 'bg-cyber-yellow/20 text-cyber-yellow'
                        : 'bg-text-muted/20 text-text-muted'
                  )}>
                    <Ban className="w-3 h-3" />
                    <span>Abort Strikes: {formatStrikeCount(votingPower.abortStrikeCount).text}</span>
                  </div>
                )}
                {votingPower.proposeStrikeCount > 0 && (
                  <div className={cn(
                    "flex items-center space-x-2 px-3 py-1 rounded-cyber",
                    formatStrikeCount(votingPower.proposeStrikeCount).level === 'danger'
                      ? 'bg-status-error/20 text-status-error'
                      : formatStrikeCount(votingPower.proposeStrikeCount).level === 'warning'
                        ? 'bg-cyber-yellow/20 text-cyber-yellow'
                        : 'bg-text-muted/20 text-text-muted'
                  )}>
                    <AlertTriangle className="w-3 h-3" />
                    <span>Propose Strikes: {formatStrikeCount(votingPower.proposeStrikeCount).text}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="absolute inset-0 bg-cyber-gradient opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none" />
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 glass-dark rounded-cyber p-1">
            {(['all', 'active', 'completed'] as const).map((filterType) => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-cyber transition-all duration-300 capitalize",
                  filter === filterType
                    ? "bg-cyber-cyan text-dark-bg shadow-neon-cyan"
                    : "text-text-secondary hover:text-cyber-cyan hover:bg-dark-panel/30"
                )}
                aria-pressed={filter === filterType}
                aria-label={`Filter by ${filterType} proposals`}
              >
                {filterType}
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'newest' | 'ending' | 'popular')}
            className="cyber-input text-sm"
            aria-label="Sort proposals"
          >
            <option value="newest">Newest First</option>
            <option value="ending">Ending Soon</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search proposals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cyber-input pl-10 w-64"
            aria-label="Search proposals"
          />
        </div>
      </div>

      {/* Proposals Grid */}
      <div className="space-y-4 lg:space-y-6">
        {filteredProposals.map((proposal) => {
          const statusDisplay = getStatusDisplay(proposal.status)
          const StatusIcon = statusDisplay.icon
          const CategoryIcon = getCategoryIcon(proposal.category)
          const userVote = userVotes[proposal.id]
          const votingProgress = getVotingProgress(proposal)
          const isVoting = votingOnProposal === proposal.id
          const forPercentage = proposal.totalVotes > 0 ? (proposal.votesFor / (proposal.votesFor + proposal.votesAgainst)) * 100 : 0
          const againstPercentage = 100 - forPercentage
          // V3.1: Get abort vote status info
          const abortStatus = formatAbortVoteStatus(proposal)
          const hasVotedAbort = userWallet ? hasUserVotedAbort(proposal, userWallet) : false

          return (
            <div 
              key={proposal.id} 
              className={cn(
                "glass-card hover:scale-102 transition-all duration-300 group cursor-pointer overflow-hidden",
                // V3.1: Add special styling for aborted proposals
                proposal.status === 'aborted' && "border-cyber-magenta/50"
              )}
              role="article"
              aria-label={`Proposal: ${proposal.title}`}
            >
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <CategoryIcon className="w-4 h-4 text-cyber-cyan" aria-hidden="true" />
                        <span className="text-xs text-cyber-cyan uppercase tracking-wide font-medium">
                          {proposal.category}
                        </span>
                      </div>
                      
                      <div className={cn(
                        "text-xs font-medium uppercase tracking-wide",
                        getPriorityColor(proposal.priority)
                      )}>
                        {proposal.priority}
                      </div>

                      {/* V3.1: Abort vote indicator */}
                      {abortStatus.hasAbortVotes && proposal.status === 'active' && (
                        <div className="flex items-center space-x-1 px-2 py-0.5 bg-cyber-magenta/20 text-cyber-magenta rounded text-xs">
                          <Ban className="w-3 h-3" aria-hidden="true" />
                          <span>{proposal.abortVoteCount}/2 Abort</span>
                        </div>
                      )}
                    </div>

                    <h4
                      className="text-base sm:text-lg font-semibold text-text-primary group-hover:text-cyber-cyan transition-colors duration-300 cursor-pointer truncate"
                      onClick={() => onViewProposal(proposal.id)}
                    >
                      {proposal.title}
                    </h4>
                    
                    <p className="text-text-secondary text-sm leading-relaxed line-clamp-2">
                      {proposal.description}
                    </p>

                    {proposal.tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        {proposal.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-dark-surface/50 text-xs text-text-muted rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end space-y-2">
                    <div className={cn(
                      "flex items-center space-x-2 px-3 py-1 rounded-cyber text-sm font-medium",
                      statusDisplay.bgColor,
                      statusDisplay.borderColor,
                      "border"
                    )}>
                      <StatusIcon className={cn("w-4 h-4", statusDisplay.color)} aria-hidden="true" />
                      <span className={statusDisplay.color}>{statusDisplay.text}</span>
                    </div>

                    {proposal.status === 'active' && (
                      <div className="flex items-center space-x-1 text-xs text-text-muted">
                        <Clock className="w-3 h-3" aria-hidden="true" />
                        <span>{getTimeRemaining(proposal.endTime)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Voting Progress */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Voting Progress</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-status-success">
                        {proposal.votesFor} For
                      </span>
                      <span className="text-status-error">
                        {proposal.votesAgainst} Against
                      </span>
                      <span className="text-text-primary">
                        {proposal.totalVotes} / {proposal.quorumRequired}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bars */}
                  <div className="space-y-2">
                    {/* Quorum Progress */}
                    <div className="progress-bar" role="progressbar" aria-valuenow={votingProgress} aria-valuemin={0} aria-valuemax={100}>
                      <div 
                        className="progress-fill transition-all duration-700 ease-cyber"
                        style={{ width: `${votingProgress}%` }}
                      />
                    </div>

                    {/* For/Against Distribution */}
                    {proposal.totalVotes > 0 && (
                      <div className="flex h-2 rounded-cyber overflow-hidden bg-dark-surface">
                        <div 
                          className="bg-status-success transition-all duration-700 ease-cyber"
                          style={{ width: `${forPercentage}%` }}
                          role="progressbar"
                          aria-valuenow={forPercentage}
                          aria-label="For votes percentage"
                        />
                        <div 
                          className="bg-status-error transition-all duration-700 ease-cyber"
                          style={{ width: `${againstPercentage}%` }}
                          role="progressbar"
                          aria-valuenow={againstPercentage}
                          aria-label="Against votes percentage"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-xs text-text-muted">
                    <span>Quorum: {votingProgress.toFixed(1)}%</span>
                    {proposal.totalVotes > 0 && (
                      <span>
                        {forPercentage.toFixed(1)}% For • {againstPercentage.toFixed(1)}% Against
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-dark-border/30">
                  {/* 
                   * UI FIX (2026-01-13): Removed non-functional "Discuss" button from proposal metadata section
                   * The Discuss button had no functionality and was removed to improve UX
                   * Discussion feature can be re-added when implemented
                   */}
                  <div className="flex items-center space-x-4 text-sm text-text-muted">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" aria-hidden="true" />
                      <span>{proposal.totalVotes} votes</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" aria-hidden="true" />
                      <span>{proposal.startTime.toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* V3.1: Active proposal voting buttons (including abort) */}
                  {proposal.status === 'active' && connected && (
                    <div className="flex items-center space-x-3">
                      {userVote ? (
                        <div className={cn(
                          "flex items-center space-x-2 px-3 py-2 rounded-cyber text-sm font-medium",
                          userVote === 'for' ? "bg-status-success/20 text-status-success" : 
                          userVote === 'against' ? "bg-status-error/20 text-status-error" :
                          userVote === 'abort' ? "bg-cyber-magenta/20 text-cyber-magenta" :
                          "bg-text-muted/20 text-text-muted"
                        )}>
                          {userVote === 'for' ? <ThumbsUp className="w-4 h-4" /> : 
                           userVote === 'against' ? <ThumbsDown className="w-4 h-4" /> :
                           userVote === 'abort' ? <Ban className="w-4 h-4" /> :
                           <Clock className="w-4 h-4" />}
                          <span>Voted {userVote}</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleVote(proposal.id, 'for')
                            }}
                            disabled={isVoting}
                            className="cyber-button-success flex items-center space-x-2 px-4 py-2 disabled:opacity-50"
                            aria-label="Vote for this proposal"
                          >
                            <ThumbsUp className="w-4 h-4" />
                            <span>For</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleVote(proposal.id, 'against')
                            }}
                            disabled={isVoting}
                            className="cyber-button-secondary flex items-center space-x-2 px-4 py-2 border-status-error text-status-error hover:bg-status-error hover:text-white disabled:opacity-50"
                            aria-label="Vote against this proposal"
                          >
                            <ThumbsDown className="w-4 h-4" />
                            <span>Against</span>
                          </button>

                          {/* V3.1: Abort vote button */}
                          {onVoteAbort && !hasVotedAbort && proposal.abortVoteCount < 2 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleVoteAbort(proposal.id)
                              }}
                              disabled={isVoting}
                              className="cyber-button-secondary flex items-center space-x-2 px-4 py-2 border-cyber-magenta text-cyber-magenta hover:bg-cyber-magenta hover:text-white disabled:opacity-50"
                              aria-label="Vote to abort this proposal"
                              title="Vote to abort this proposal. Warning: If proposal passes, abort voters receive a strike."
                            >
                              <Ban className="w-4 h-4" />
                              <span>Abort</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* V3.1: Finalize abort button (when 2 abort votes reached) */}
                      {abortStatus.canBeAborted && onAbortProposal && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAbortProposal(proposal.id)
                          }}
                          disabled={abortingProposal}
                          className="cyber-button-secondary flex items-center space-x-2 px-4 py-2 bg-cyber-magenta/20 border-cyber-magenta text-cyber-magenta hover:bg-cyber-magenta hover:text-white disabled:opacity-50"
                          aria-label="Finalize proposal abortion"
                        >
                          <AlertOctagon className="w-4 h-4" />
                          <span>{abortingProposal ? 'Aborting...' : 'Finalize Abort'}</span>
                        </button>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onViewProposal(proposal.id)
                        }}
                        className="cyber-button flex items-center space-x-1 px-3 py-2"
                        aria-label="View proposal details"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View</span>
                      </button>
                    </div>
                  )}

                  {/* Execute button for passed proposals */}
                  {proposal.status === 'passed' && connected && onExecuteProposal && (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleExecuteProposal()
                        }}
                        disabled={executingProposal}
                        className="cyber-button-primary flex items-center space-x-2 px-4 py-2 disabled:opacity-50"
                        aria-label="Execute this proposal"
                      >
                        <Zap className="w-4 h-4" />
                        <span>{executingProposal ? 'Executing...' : 'Execute Proposal'}</span>
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onViewProposal(proposal.id)
                        }}
                        className="cyber-button flex items-center space-x-1 px-3 py-2"
                        aria-label="View proposal details"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View</span>
                      </button>
                    </div>
                  )}

                  {/* V3.1: Aborted proposals - just show view button */}
                  {proposal.status === 'aborted' && (
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2 px-3 py-2 bg-cyber-magenta/20 text-cyber-magenta rounded-cyber text-sm">
                        <AlertOctagon className="w-4 h-4" />
                        <span>Proposal Aborted</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onViewProposal(proposal.id)
                        }}
                        className="cyber-button flex items-center space-x-1 px-3 py-2"
                        aria-label="View proposal details"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute inset-0 bg-cyber-gradient opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none rounded-cyber" />
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {filteredProposals.length === 0 && (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 bg-dark-surface rounded-cyber flex items-center justify-center mx-auto mb-4">
            <Vote className="w-8 h-8 text-text-muted" aria-hidden="true" />
          </div>
          <h4 className="text-lg font-medium text-text-primary mb-2">
            {searchTerm ? 'No matching proposals' : 'No proposals found'}
          </h4>
          <p className="text-text-muted mb-6">
            {searchTerm 
              ? 'Try adjusting your search terms or filters.'
              : 'Be the first to create a proposal for this pool.'}
          </p>
          {!searchTerm && (
            <button
              onClick={onCreateProposal}
              disabled={!connected}
              className="cyber-button-primary flex items-center space-x-2 mx-auto disabled:opacity-50"
              aria-label="Create a new proposal"
            >
              <Plus className="w-4 h-4" />
              <span>Create Proposal</span>
            </button>
          )}
        </div>
      )}

      {/* Connection prompt for non-connected users */}
      {!connected && proposals.length > 0 && (
        <div className="glass-card p-6 text-center border border-cyber-cyan/20">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <Vote className="w-5 h-5 text-cyber-cyan" aria-hidden="true" />
            <h4 className="text-lg font-medium text-cyber-cyan">Connect Wallet to Participate</h4>
          </div>
          <p className="text-text-muted">
            Connect your wallet to vote on proposals and participate in governance.
          </p>
        </div>
      )}
    </div>
  )
}

export default GovernancePanel
